/**
 * Cart recommendations — refresh + add-to-cart + tracking.
 *
 * Cross-browser: written in ES5 (no arrow functions, async/await, spread, for…of,
 * template literals) so it runs on phones, desktops/laptops, in-app browsers
 * (Facebook/Instagram/TikTok) and smart-TV browsers (Samsung Tizen, LG webOS,
 * Android TV). Newer APIs are feature-detected with fallbacks:
 *   - fetch            → falls back to a normal form POST to /cart/add
 *   - AbortController  → optional
 *   - PerformanceObserver (cart change detection) → also listens to theme cart events
 *   - IntersectionObserver / checkVisibility → optional
 *   - DOMParser        → falls back to a detached <div>
 *
 * - Re-renders [data-cart-recs] via /?sections=cart-recs after any cart change.
 * - Adds via /cart/add.js, then refreshes the drawer (Dawn renderContents or Focal cart:refresh).
 * - Never replaces or wraps window.fetch.
 */
(function () {
  'use strict';
  if (window.CartRecs) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTION = 'cart-recs';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  var OPEN_THROTTLE_MS = 30000; // refresh-on-open at most every 30s
  var lastCheck = Date.now();   // server-rendered module is fresh on page load
  var timer = null;
  var controller = null;
  var viewObserver = null;

  // ---------------------------------------------------------------------------
  // Small helpers (polyfills scoped to this file — nothing global is changed)
  // ---------------------------------------------------------------------------
  function toArray(list) { return Array.prototype.slice.call(list || []); }

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) el = el && el.parentElement;
    if (!el) return null;
    if (el.closest) return el.closest(selector);
    var matches = el.matches || el.webkitMatchesSelector || el.msMatchesSelector;
    while (el && el.nodeType === 1) {
      if (matches.call(el, selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function extend(target, source) {
    for (var k in source) {
      if (Object.prototype.hasOwnProperty.call(source, k)) target[k] = source[k];
    }
    return target;
  }

  function emit(target, name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, detail: detail });
    } catch (err) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    target.dispatchEvent(ev);
  }

  function clearChildren(el) { while (el.firstChild) el.removeChild(el.firstChild); }

  function parseModule(html) {
    var doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
      if (doc) return doc.querySelector('[data-cart-recs]');
    } catch (err) { /* fall through */ }
    var div = document.createElement('div');
    div.innerHTML = html;
    return div.querySelector('[data-cart-recs]');
  }

  function warn(msg, err) {
    if (window.console && console.warn) console.warn('[cart-recs] ' + msg, err || '');
  }

  // ---------------------------------------------------------------------------
  // Refresh the module from the server (only touches the DOM if it changed)
  // ---------------------------------------------------------------------------
  function refresh() {
    lastCheck = Date.now();
    if (!document.querySelector('[data-cart-recs]') || !window.fetch) return;

    if (controller && controller.abort) controller.abort();
    controller = window.AbortController ? new AbortController() : null;
    var opts = controller ? { signal: controller.signal, credentials: 'same-origin' } : { credentials: 'same-origin' };

    window.fetch(ROOT + '?sections=' + SECTION, opts)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        if (!json || !json[SECTION]) return;
        var fresh = parseModule(json[SECTION]);
        if (!fresh) return;
        // Apps like Extend Checkout+ watch the drawer for DOM changes; replacing
        // identical markup can make them re-mount, so skip unchanged modules.
        toArray(document.querySelectorAll('[data-cart-recs]')).forEach(function (el) {
          if (el.outerHTML !== fresh.outerHTML && el.parentNode) {
            el.parentNode.replaceChild(fresh.cloneNode(true), el);
          }
        });
        observeView();
        trackView();
      })
      ['catch'](function (err) {
        if (!err || err.name !== 'AbortError') warn('refresh failed', err);
      });
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 150);
  }

  function refreshIfStale() {
    if (Date.now() - lastCheck < OPEN_THROTTLE_MS) return;
    refresh();
  }

  // ---------------------------------------------------------------------------
  // Detect cart changes made by the theme or other apps — without patching fetch.
  // 1) Resource Timing (modern browsers): reads finished fetch/XHR request URLs.
  // 2) Theme cart events (all browsers, incl. older Safari/TVs): Focal & others.
  // refresh() ignores unchanged markup, so overlapping triggers are harmless.
  // ---------------------------------------------------------------------------
  (function watchCartRequests() {
    if (!window.PerformanceObserver) return;
    try {
      var po = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          var en = entries[i];
          if (en.initiatorType !== 'fetch' && en.initiatorType !== 'xmlhttprequest') continue;
          var path = '';
          try { path = new URL(en.name, window.location.href).pathname; } catch (e) { continue; }
          if (CART_MUTATION.test(path)) { scheduleRefresh(); break; }
        }
      });
      po.observe({ entryTypes: ['resource'] });
    } catch (err) {
      warn('cart watcher unavailable', err);
    }
  })();

  ['cart:refresh', 'cart:updated', 'cart:change', 'cart:rerender', 'variant:added', 'cart-drawer:refresh']
    .forEach(function (name) {
      document.addEventListener(name, scheduleRefresh);
      document.documentElement.addEventListener(name, scheduleRefresh);
    });

  // ---------------------------------------------------------------------------
  // Price + error UI
  // ---------------------------------------------------------------------------
  function priceNode(tag, label, amount, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    var sr = document.createElement('span');
    sr.className = 'cart-recs__sr';
    sr.appendChild(document.createTextNode(label));
    node.appendChild(sr);
    node.appendChild(document.createTextNode(amount || ''));
    return node;
  }

  function setPrice(el, price, compare) {
    if (!el) return;
    clearChildren(el);
    if (compare) {
      el.appendChild(priceNode('s', 'Regular price', compare));
      el.appendChild(document.createTextNode(' '));
      el.appendChild(priceNode('span', 'Sale price', price, 'cart-recs__price-sale'));
    } else {
      el.appendChild(priceNode('span', 'Price', price));
    }
  }

  function setError(card, message) {
    var el = card && card.querySelector('[data-cart-recs-error]');
    if (!el) return;
    el.textContent = message || '';
    if (message) { el.removeAttribute('hidden'); el.style.display = ''; }
    else { el.setAttribute('hidden', ''); el.style.display = 'none'; }
  }

  // Variant select → update price + button variant
  document.addEventListener('change', function (e) {
    var select = closest(e.target, '[data-cart-recs-variant]');
    if (!select) return;
    var card = closest(select, '[data-cart-recs-card]');
    var option = select.options[select.selectedIndex];
    if (!card || !option) return;
    var btn = card.querySelector('[data-cart-recs-add]');
    if (btn) btn.setAttribute('data-variant-id', select.value);
    var amount = option.getAttribute('data-amount');
    if (amount) card.setAttribute('data-price', amount);
    setPrice(card.querySelector('[data-cart-recs-price]'),
      option.getAttribute('data-price'), option.getAttribute('data-compare'));
    setError(card, '');
  });

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------
  // No fetch (very old browsers): submit a normal form, the theme's standard cart flow.
  function addWithForm(variantId, props) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = ROOT + 'cart/add';
    form.style.display = 'none';
    function field(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      form.appendChild(input);
    }
    field('id', variantId);
    field('quantity', '1');
    for (var k in props) {
      if (Object.prototype.hasOwnProperty.call(props, k)) field('properties[' + k + ']', props[k]);
    }
    document.body.appendChild(form);
    form.submit();
  }

  document.addEventListener('click', function (e) {
    var btn = closest(e.target, '[data-cart-recs-add]');
    if (!btn || btn.disabled) return;
    if (e.preventDefault) e.preventDefault();

    var card = closest(btn, '[data-cart-recs-card]');
    var addModule = closest(btn, '[data-cart-recs]');
    var variantId = btn.getAttribute('data-variant-id');
    var addScenario = addModule ? (addModule.getAttribute('data-scenario') || '') : '';
    var addPlacement = placementOf(addModule);
    var props = { _rec_source: 'cart-recs', _rec_scenario: addScenario, _rec_placement: addPlacement };
    var addData = extend(cardData(card), { variant_id: variantId });

    if (!window.fetch) {
      publish('cart_recs_added', { scenario: addScenario, placement: addPlacement, currency: currency(), product: addData });
      addWithForm(variantId, props);
      return;
    }

    var drawer = document.querySelector('cart-drawer');
    var dawn = !!(drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function');
    var label = btn.textContent;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding\u2026';
    setError(card, '');

    window.fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: props }],
        sections: dawn ? drawer.getSectionsToRender().map(function (s) { return s.id; }) : [],
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        return res.json()['catch'](function () { return {}; }).then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'This item couldn\u2019t be added. Please try again.');
          }
          return data;
        });
      })
      .then(function (data) {
        if (dawn && data.sections) {
          drawer.renderContents(data); // Dawn: re-renders lines, totals and this module
        } else {
          emit(document.documentElement, 'cart:refresh'); // Focal re-renders its drawer
          scheduleRefresh();
        }
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recs', cartData: data });
        }
        publish('cart_recs_added', { scenario: addScenario, placement: addPlacement, currency: currency(), product: addData });

        // Cart page doesn't re-render from cart:refresh → reload (short delay lets tracking send)
        if (addPlacement === 'cart_page') {
          setTimeout(function () { window.location.reload(); }, 300);
          return;
        }
        emit(document, 'cart-recs:added', data);
        // Button may still exist if the drawer didn't re-render
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      })
      ['catch'](function (err) {
        setError(card, (err && err.message) || 'This item couldn\u2019t be added. Please try again.');
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      });
  });

  // ---------------------------------------------------------------------------
  // Tracking (Shopify Customer Events → custom pixel → GA4). Never breaks the cart.
  // ---------------------------------------------------------------------------
  function publish(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (err) { /* ignore */ }
  }

  function currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  }

  function cardData(card) {
    if (!card) return {};
    var btn = card.querySelector('[data-cart-recs-add]');
    return {
      product_id: card.getAttribute('data-product-id'),
      variant_id: btn ? btn.getAttribute('data-variant-id') : null,
      title: card.getAttribute('data-product-title'),
      type: card.getAttribute('data-product-type'),
      vendor: card.getAttribute('data-product-vendor'),
      price: Number(card.getAttribute('data-price')) || 0,
      position: Number(card.getAttribute('data-position')) || 1
    };
  }

  // On screen: not hidden and inside the viewport (a closed drawer may be hidden or off-screen)
  function isShown(el) {
    if (!el) return false;
    try {
      if (typeof el.checkVisibility === 'function' &&
          !el.checkVisibility({ visibilityProperty: true, opacityProperty: true })) return false;
    } catch (err) { /* older API shape */ }
    if (el.offsetParent === null) return false;
    if (window.getComputedStyle) {
      var node = el;
      while (node && node.nodeType === 1) {
        var cs = window.getComputedStyle(node);
        if (cs.visibility === 'hidden' || cs.opacity === '0') return false;
        node = node.parentElement;
      }
    }
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.right > 0 && r.left < vw && r.bottom > 0 && r.top < vh;
  }

  function placementOf(el) {
    var wrap = closest(el, '[data-cart-recs-placement]');
    return wrap ? wrap.getAttribute('data-cart-recs-placement') : 'drawer';
  }

  // Each on-screen module is tracked once per session per placement + scenario + products
  function trackView() {
    toArray(document.querySelectorAll('[data-cart-recs]')).forEach(function (module) {
      if (!isShown(module)) return;
      var cards = toArray(module.querySelectorAll('[data-cart-recs-card]'));
      if (!cards.length) return;
      var scenario = module.getAttribute('data-scenario') || '';
      var placement = placementOf(module);
      var products = cards.map(cardData);
      var key = 'cart_recs_viewed:' + placement + ':' + scenario + ':' +
        products.map(function (p) { return p.product_id; }).join(',');
      try {
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, '1');
      } catch (err) { /* storage blocked (private mode / TV): still send */ }
      publish('cart_recs_viewed', { scenario: scenario, placement: placement, currency: currency(), products: products });
    });
  }

  // Count a view when the module scrolls into view (it can sit below the fold on phones)
  function observeView() {
    if (!window.IntersectionObserver) return;
    if (viewObserver) viewObserver.disconnect();
    var modules = toArray(document.querySelectorAll('[data-cart-recs]'));
    if (!modules.length) return;
    viewObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { trackView(); break; }
      }
    }, { threshold: 0.3 });
    modules.forEach(function (m) { viewObserver.observe(m); });
  }

  // ---------------------------------------------------------------------------
  // Drawer open → re-check stock (throttled) and count the view
  // ---------------------------------------------------------------------------
  function onDrawerOpened() {
    setTimeout(function () { observeView(); trackView(); }, 400); // after open animation
    refreshIfStale();
  }

  if (window.MutationObserver) {
    var openObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var el = m.target;
        if (!el || el.nodeType !== 1) continue;
        var opened =
          (m.attributeName === 'open' && el.hasAttribute('open')) ||
          (m.attributeName === 'aria-hidden' && el.getAttribute('aria-hidden') === 'false');
        if (opened && el.querySelector('[data-cart-recs]')) { onDrawerOpened(); break; }
      }
    });
    openObserver.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ['open', 'aria-hidden']
    });
  }

  // Coming back to the tab/app with the cart open
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    if (isShown(document.querySelector('[data-cart-recs]'))) refreshIfStale();
  });

  function initView() { observeView(); trackView(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initView);
  } else {
    initView();
  }

  window.CartRecs = { refresh: refresh, refreshIfStale: refreshIfStale, trackView: trackView };
})();
